import { BuildingOfficeIcon, HomeIcon, ShareIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CustomListItemId,
    CustomListKey,
    formatCalendarDate,
    formatDateNoTime,
    HomeCareLeadId,
    PlacementId,
    RegionId,
    TenantPartnershipId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { sharedLeadPath } from "components/shared-pipelines/shared-pipeline-tabs";
import React from "react";
import { Link } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import { useRegionId } from "store/selectors/hooks";

export type ClientEngagement = inferProcedureOutput<
    AppRouter["clientEngagements"]["list"]
>[number];

export type OwnEngagement = Exclude<ClientEngagement, { type: "sharedHomeCareLead" }>;

export type OwnEngagementRef =
    | { kind: "placement"; id: PlacementId }
    | { kind: "homeCareLead"; id: HomeCareLeadId };

export type EngagementRef =
    | OwnEngagementRef
    | {
          kind: "sharedHomeCareLead";
          id: HomeCareLeadId;
          partnershipId: TenantPartnershipId;
          regionId: RegionId;
      };

export type EngagementLink =
    | { type: "placement"; id: PlacementId }
    | { type: "homeCareLead"; id: HomeCareLeadId }
    | {
          type: "sharedHomeCareLead";
          id: HomeCareLeadId;
          partnershipId: TenantPartnershipId;
      };

export const engagementPath = (engagement: EngagementLink): string => {
    switch (engagement.type) {
        case "placement":
            return `/clients/show/${engagement.id}`;
        case "homeCareLead":
            return `/home-care-clients/show/${engagement.id}`;
        case "sharedHomeCareLead":
            return sharedLeadPath(engagement.partnershipId, engagement.id);
        default: {
            const exhaustiveCheck: never = engagement;
            throw new Error(`Unhandled engagement ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

const isOwnEngagement = (engagement: ClientEngagement): engagement is OwnEngagement =>
    engagement.type !== "sharedHomeCareLead";

export const engagementsForRegion = (
    engagements: ClientEngagement[],
    regionId: RegionId | null,
): ClientEngagement[] =>
    engagements.filter(
        engagement =>
            isOwnEngagement(engagement) ||
            (regionId !== null && engagement.regionIds.includes(regionId)),
    );

export const engagementDateLabel = (
    engagement: ClientEngagement,
    today: CalendarDate,
): string => {
    const startedLabel = `Started ${formatDateNoTime(engagement.dateAdded, "")}`;
    switch (engagement.type) {
        case "placement": {
            const moved = CalendarDate.parse(
                engagement.moveTimelineActual ?? "",
            ).getOrElse(null);
            if (!moved) {
                return startedLabel;
            }
            const verb = moved.lessThan(today) ? "Moved" : "Moving";
            return `${verb} ${formatCalendarDate(moved, "")}`;
        }
        case "homeCareLead":
        case "sharedHomeCareLead": {
            const startOfCare = CalendarDate.parse(
                engagement.actualStartDate ?? "",
            ).getOrElse(null);
            if (!startOfCare) {
                return startedLabel;
            }
            const verb = startOfCare.lessThan(today) ? "Care started" : "Care starts";
            return `${verb} ${formatCalendarDate(startOfCare, "")}`;
        }
        default: {
            const exhaustiveCheck: never = engagement;
            throw new Error(`Unhandled engagement ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

interface EngagementsQueryResult {
    engagements: ClientEngagement[] | undefined;
}

const useClientEngagements = (current: EngagementRef): EngagementsQueryResult => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const options = trpcClient.clientEngagements.list.queryOptions({
        ref: current,
        sharedPipelines: "include",
    });
    const pathKey = trpcClient.clientEngagements.list.pathKey();
    const query = useQuery({
        ...options,
        placeholderData: () => {
            const cachedLists = queryClient.getQueriesData<ClientEngagement[]>({
                queryKey: pathKey,
            });
            for (const [, cachedList] of cachedLists) {
                if (cachedList?.some(engagement => engagement.id === current.id)) {
                    return cachedList;
                }
            }
            return undefined;
        },
    });
    return { engagements: query.data };
};

const engagementListPath = (kind: OwnEngagementRef["kind"]): string => {
    switch (kind) {
        case "placement":
            return "/clients";
        case "homeCareLead":
            return "/home-care-pipeline";
        default: {
            const exhaustiveCheck: never = kind;
            throw new Error(`Unhandled engagement kind ${String(exhaustiveCheck)}`);
        }
    }
};

export const deleteNavigationPath = (
    current: OwnEngagementRef,
    engagements: ClientEngagement[],
): string => {
    const own = engagements.filter(isOwnEngagement);
    const index = own.findIndex(engagement => engagement.id === current.id);
    const target = index >= 0 ? (own[index - 1] ?? own[index + 1]) : own[0];
    return target && target.id !== current.id
        ? engagementPath(target)
        : engagementListPath(current.kind);
};

const pruneEngagementFromCaches = (
    queryClient: QueryClient,
    pathKey: readonly unknown[],
    deletedId: PlacementId | HomeCareLeadId,
): void => {
    const cachedLists = queryClient.getQueriesData<ClientEngagement[]>({
        queryKey: pathKey,
    });
    for (const [queryKey, cachedList] of cachedLists) {
        if (cachedList?.some(engagement => engagement.id === deletedId)) {
            queryClient.setQueryData(
                queryKey,
                cachedList.filter(engagement => engagement.id !== deletedId),
            );
        }
    }
};

export const useDeleteNavigation = (current: OwnEngagementRef): (() => string) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const pathKey = trpcClient.clientEngagements.list.pathKey();
    const { engagements } = useClientEngagements(current);
    return () => {
        const path = deleteNavigationPath(current, engagements ?? []);
        pruneEngagementFromCaches(queryClient, pathKey, current.id);
        return path;
    };
};

const tabClasses =
    "flex-shrink-0 min-w-[10.5rem] rounded-lg px-3 py-2 text-left transition";
const currentTabClasses = `${tabClasses} border-2 border-sky-600 bg-sky-50`;
const otherTabClasses = `${tabClasses} border border-gray-200 bg-white hover:border-sky-300`;

interface EngagementTabPresentation {
    TypeIcon: typeof BuildingOfficeIcon;
    typeLabel: string;
    statusName: string;
}

export type EngagementOwnership = "own" | "acrossShare";

const ownTypeLabel = (typeLabel: string, ownership: EngagementOwnership): string =>
    ownership === "acrossShare" ? `Your client · ${typeLabel}` : typeLabel;

export const engagementTabPresentation = (
    engagement: ClientEngagement,
    ownership: EngagementOwnership,
    statusNames: {
        placement: (id: CustomListItemId) => string;
        homeCareLead: (id: CustomListItemId) => string;
    },
): EngagementTabPresentation => {
    switch (engagement.type) {
        case "placement":
            return {
                TypeIcon: BuildingOfficeIcon,
                typeLabel: ownTypeLabel("Placement", ownership),
                statusName: statusNames.placement(engagement.statusListItemId),
            };
        case "homeCareLead":
            return {
                TypeIcon: HomeIcon,
                typeLabel: ownTypeLabel("Home Care", ownership),
                statusName: statusNames.homeCareLead(engagement.statusListItemId),
            };
        case "sharedHomeCareLead":
            return {
                TypeIcon: ShareIcon,
                typeLabel: `Shared by ${engagement.sendingTenantName}`,
                statusName: engagement.statusName ?? "",
            };
        default: {
            const exhaustiveCheck: never = engagement;
            throw new Error(`Unhandled engagement ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

export const engagementOwnership = (
    engagements: ClientEngagement[],
): EngagementOwnership =>
    engagements.some(engagement => !isOwnEngagement(engagement)) ? "acrossShare" : "own";

interface EngagementTabContentProps {
    engagement: ClientEngagement;
    ownership: EngagementOwnership;
    current: boolean;
}

const EngagementTabContent: React.FC<EngagementTabContentProps> = props => {
    const { engagement, ownership, current } = props;
    const { getListItemName: getPlacementStatusName } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { getListItemName: getHomeCareStatusName } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );
    const { TypeIcon, typeLabel, statusName } = engagementTabPresentation(
        engagement,
        ownership,
        {
            placement: getPlacementStatusName,
            homeCareLead: getHomeCareStatusName,
        },
    );
    return (
        <>
            <span
                className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide ${
                    current ? "text-sky-800" : "text-gray-500"
                }`}>
                <TypeIcon className="size-3.5 flex-shrink-0" />
                {typeLabel}
            </span>
            <span className="mt-0.5 block text-sm font-semibold text-gray-900 leading-snug">
                {statusName}
            </span>
            <span className="block text-xs text-gray-500">
                {engagementDateLabel(engagement, CalendarDate.todayNoTime)}
            </span>
        </>
    );
};

interface EngagementTabsProps {
    current: EngagementRef;
}

const isCurrentEngagement = (
    engagement: ClientEngagement,
    current: EngagementRef,
): boolean => {
    const sharedEngagement = !isOwnEngagement(engagement);
    const sharedRef = current.kind === "sharedHomeCareLead";
    return engagement.id === current.id && sharedEngagement === sharedRef;
};

export const EngagementTabs: React.FC<EngagementTabsProps> = props => {
    const { current } = props;
    const regionId = useRegionId();
    const { engagements: listed } = useClientEngagements(current);
    const engagements = React.useMemo(
        () => (listed ? engagementsForRegion(listed, regionId) : undefined),
        [listed, regionId],
    );

    if (!engagements || engagements.length < 2) {
        return null;
    }
    const ownership = engagementOwnership(engagements);

    return (
        <div className="px-4 pb-3">
            <div className="flex items-stretch gap-2 overflow-x-auto p-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {engagements.map(engagement =>
                    isCurrentEngagement(engagement, current) ? (
                        <div
                            key={`${engagement.type}-${engagement.id}`}
                            aria-current="page"
                            className={currentTabClasses}>
                            <EngagementTabContent
                                engagement={engagement}
                                ownership={ownership}
                                current={true}
                            />
                        </div>
                    ) : (
                        <Link
                            key={`${engagement.type}-${engagement.id}`}
                            to={engagementPath(engagement)}
                            className={otherTabClasses}>
                            <EngagementTabContent
                                engagement={engagement}
                                ownership={ownership}
                                current={false}
                            />
                        </Link>
                    ),
                )}
            </div>
        </div>
    );
};
